import { styled } from '@mui/material/styles';
import { Box, Paper, TextField, Select, Typography, Button, FormControl, Alert } from '@mui/material';

// Honey-inspired color palette
export const honeyGeneralColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main container
export const HoneyGeneralContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyGeneralColors.honeyBrown,
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(3, 0),
}));

// Header section
export const HoneyGeneralHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Title with honey gradient
export const HoneyGeneralTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyGeneralColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle
export const HoneyGeneralSubtitle = styled(Typography)(({ theme }) => ({
  color: honeyGeneralColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// Form container
export const HoneyFormContainer = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  
  // Honey accent stripe (but no hexagon dot)
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${honeyGeneralColors.goldenHoney} 0%, ${honeyGeneralColors.brightHoney} 50%, ${honeyGeneralColors.honeycomb} 100%)`,
    borderRadius: '20px 20px 0 0',
  },
}));

// Form field styling
export const HoneyTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: honeyGeneralColors.brightHoney,
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyGeneralColors.goldenHoney}20`,
    },
    
    '&.Mui-error fieldset': {
      borderColor: honeyGeneralColors.warmAmber,
    },
  },
  
  '& .MuiInputBase-input': {
    fontSize: '1rem',
    color: honeyGeneralColors.honeyBrown,
    padding: theme.spacing(1.5),
    
    '&::placeholder': {
      color: honeyGeneralColors.lightBrown,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyGeneralColors.honeyBrown,
    fontWeight: 600,
    fontSize: '0.95rem',
    
    '&.Mui-focused': {
      color: honeyGeneralColors.goldenHoney,
    },
    
    '&.Mui-error': {
      color: honeyGeneralColors.warmAmber,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.85rem',
    color: honeyGeneralColors.lightBrown,
    fontStyle: 'italic',
    
    '&.Mui-error': {
      color: honeyGeneralColors.warmAmber,
    },
  },
}));

// Form control for select
export const HoneyFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: honeyGeneralColors.brightHoney,
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyGeneralColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyGeneralColors.goldenHoney}20`,
    },
    
    '&.Mui-error fieldset': {
      borderColor: honeyGeneralColors.warmAmber,
    },
  },
  
  '& .MuiSelect-select': {
    fontSize: '1rem',
    color: honeyGeneralColors.honeyBrown,
    padding: theme.spacing(1.5),
  },
  
  '& .MuiInputLabel-root': {
    color: honeyGeneralColors.honeyBrown,
    fontWeight: 600,
    fontSize: '0.95rem',
    
    '&.Mui-focused': {
      color: honeyGeneralColors.goldenHoney,
    },
    
    '&.Mui-error': {
      color: honeyGeneralColors.warmAmber,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.85rem',
    color: honeyGeneralColors.lightBrown,
    fontStyle: 'italic',
    
    '&.Mui-error': {
      color: honeyGeneralColors.warmAmber,
    },
  },
  
  '& .MuiSelect-icon': {
    color: honeyGeneralColors.honeycomb,
  },
}));

// Status info container
export const HoneyStatusInfo = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${honeyGeneralColors.brightHoney}10, ${honeyGeneralColors.goldenHoney}10)`,
  border: `1px solid ${honeyGeneralColors.brightHoney}`,
  borderRadius: '12px',
  padding: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}));

// Status info text
export const HoneyStatusText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: honeyGeneralColors.lightBrown,
  margin: 0,
}));

// Required field indicator
export const HoneyRequired = styled('span')(({ theme }) => ({
  color: honeyGeneralColors.warmAmber,
}));

// Save button
export const HoneySaveButton = styled(Button)(({ theme }) => ({
  background: honeyGeneralColors.honeyGradient,
  color: honeyGeneralColors.honeyBrown,
  borderRadius: '50px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 700,
  fontSize: '1rem',
  boxShadow: `0 8px 25px ${honeyGeneralColors.goldenHoney}40`,
  transition: 'all 0.3s ease',
  textTransform: 'none',
  
  '&:hover': {
    background: honeyGeneralColors.honeycombGradient,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 35px ${honeyGeneralColors.goldenHoney}60`,
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
    background: honeyGeneralColors.honeyGradient,
  },
  
  '&.success': {
    background: 'linear-gradient(135deg, #90EE90, #98FB98)',
    color: '#2D5016',
  },
  
  '&.error': {
    background: `linear-gradient(135deg, ${honeyGeneralColors.warmAmber}, #D2691E)`,
    color: 'white',
  },
}));

// Cancel button
export const HoneyCancelButton = styled(Button)(({ theme }) => ({
  background: `${honeyGeneralColors.brightHoney}10`,
  color: honeyGeneralColors.honeyBrown,
  border: `2px solid ${honeyGeneralColors.brightHoney}`,
  borderRadius: '50px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  
  '&:hover': {
    background: `${honeyGeneralColors.brightHoney}20`,
    borderColor: honeyGeneralColors.goldenHoney,
  },
}));

// Alert styling
export const HoneyAlert = styled(Alert)(({ theme, severity }) => {
  const getAlertStyles = () => {
    switch (severity) {
      case 'warning':
        return {
          backgroundColor: `${honeyGeneralColors.warmAmber}15`,
          borderColor: honeyGeneralColors.warmAmber,
          color: honeyGeneralColors.honeyBrown,
          
          '& .MuiAlert-icon': {
            color: honeyGeneralColors.warmAmber,
          },
        };
      case 'info':
        return {
          backgroundColor: `${honeyGeneralColors.brightHoney}15`,
          borderColor: honeyGeneralColors.brightHoney,
          color: honeyGeneralColors.honeyBrown,
          
          '& .MuiAlert-icon': {
            color: honeyGeneralColors.goldenHoney,
          },
        };
      case 'success':
        return {
          backgroundColor: '#90EE9015',
          borderColor: '#90EE90',
          color: honeyGeneralColors.honeyBrown,
          
          '& .MuiAlert-icon': {
            color: '#90EE90',
          },
        };
      default:
        return {};
    }
  };

  return {
    borderRadius: '12px',
    border: '1px solid',
    marginBottom: theme.spacing(2),
    ...getAlertStyles(),
  };
});

// Grid spacing helper
export const HoneyFormGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  
  '&.two-column': {
    gridTemplateColumns: '1fr 1fr',
    
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

// Actions container
export const HoneyActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  marginTop: theme.spacing(3),
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));